import {Component, DestroyRef, effect, OnInit, signal, ViewChild, WritableSignal} from '@angular/core';
import {PanelComponent} from '../../../../../panel/panel.component';
import {Card} from 'primeng/card';
import {Tab, TabList, TabPanel, TabPanels, Tabs} from 'primeng/tabs';
import {Badge} from 'primeng/badge';
import {Timeline} from 'primeng/timeline';
import {ActivatedRoute} from '@angular/router';
import {VehicleFacade} from '../../facades/vehicle.facade';
import {Skeleton} from 'primeng/skeleton';
import {AsyncPipe} from '@angular/common';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {
  tablerArchive,
  tablerCircleKey,
  tablerEngine,
  tablerFolder,
  tablerKey,
  tablerLabel,
  tablerNumber123,
  tablerShieldExclamation,
  tablerTruckDelivery,
  tablerUser,
  tablerUserCircle
} from '@ng-icons/tabler-icons';
import {saxCarOutline} from '@ng-icons/iconsax/outline';
import {PrimeTemplate} from 'primeng/api';
import {Message} from 'primeng/message';
import {AvailableBannerType} from '../../../../../../core/enums/banner/available-banner-type.enum';
import {Divider} from 'primeng/divider';
import {LocationFacade} from '../../../../../location/facade/location.facade';
import {filter} from 'rxjs';
import {Geocoding} from '../../../../../location/model/geocoding.model';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {DateTime} from 'luxon';
import {Vehicle} from '../../model/vehicle.model';
import {ItemImageDto} from '../../model/item-image.model';
import {AvailablePropertyType} from '../../../../property/enum/available-property.type';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SpeedDial} from 'primeng/speeddial';
import {DynamicDialogService} from '../../../../../../shared/components/modals/dialog/service/dialog.service';
import {VehicleReleaseModalComponent} from '../../../vehicle-release/modal/component/vehicle-release-modal.component';
import {VehicleReleaseModalResult} from '../../../vehicle-release/model/vehicle-release-modal-result.model';
import {Editor, EditorTextChangeEvent} from 'primeng/editor';
import {FormControl, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {GalleriaModule} from 'primeng/galleria';
import {FileSelectEvent, FileUpload} from 'primeng/fileupload';
import {Carousel} from 'primeng/carousel';
import {Tooltip} from 'primeng/tooltip';
import {Tag} from 'primeng/tag';
import {VehicleKeyAssignModalData} from '../../../vehicle-key-assign/model/vehicle-key-assign-modal-data.model';
import {VehicleKeyAssignModalResult} from '../../../vehicle-key-assign/model/vehicle-key-assign-modal-result.model';
import {
  VehicleKeyAssignModalComponent
} from '../../../vehicle-key-assign/modal/vehicle-key-assign-modal/vehicle-key-assign-modal.component';
import {LoadingComponent} from '../../../../../../shared/components/loading/loading/loading.component';

export interface VehicleStatusEvent {
  status: string;
  date: DateTime;
  icon: string;
}

export interface VehicleGalleryImage {
  id?: number;
  itemImageSrc: string;
  thumbnailImageSrc: string;
  alt: string;
  title: string;
}

@Component({
  selector: 'app-vehicle-details',
  imports: [
    PanelComponent,
    Card,
    Tabs,
    TabPanels,
    TabPanel,
    TabList,
    Tab,
    Badge,
    Timeline,
    Skeleton,
    AsyncPipe,
    NgIcon,
    PrimeTemplate,
    Message,
    Divider,
    SpeedDial,
    Editor,
    FormsModule,
    ReactiveFormsModule,
    Button,
    GalleriaModule,
    FileUpload,
    Carousel,
    Tooltip,
    Tag,
    LoadingComponent,

  ],
  providers: [
    provideIcons({
      tablerKey,
      tablerUser,
      saxCarOutline,
      tablerNumber123,
      tablerLabel,
      tablerFolder,
      tablerArchive,
      tablerUserCircle,
      tablerTruckDelivery,
      tablerCircleKey,
      tablerEngine,
      tablerShieldExclamation
    })
  ],
  templateUrl: './vehicle-details.component.html',
  styleUrl: './vehicle-details.component.css',
})
export class VehicleDetailsComponent implements OnInit {
  protected readonly tabBannerType: AvailableBannerType = AvailableBannerType.INFO;
  protected readonly tabBannerIcon: string = 'pi pi-info-circle';

  protected statusHistory: VehicleStatusEvent[] = [];

  protected readonly vehicle: WritableSignal<Vehicle | null> = signal(null);
  protected readonly iframeUrl: WritableSignal<SafeResourceUrl | null> = signal(null);
  protected readonly vehicleId: WritableSignal<string | null> = signal(null);
  protected readonly annotationsChanged: WritableSignal<boolean> = signal<boolean>(false);
  protected selectedUploadImages: File[] = [];

  protected details: any[] = [];

  @ViewChild('imagesUpload')
  protected imagesUpload?: FileUpload;

  protected fullscreenMode: boolean = false;

  protected images: VehicleGalleryImage[] = [
    {
      itemImageSrc: '/placeholder-vehicle.svg',
      thumbnailImageSrc: '/placeholder-vehicle.svg',
      alt: 'Brak zdjęcia',
      title: 'Brak zdjęcia'
    }
  ];

  private readonly placeholderImage: VehicleGalleryImage = {
    itemImageSrc: '/placeholder-vehicle.svg',
    thumbnailImageSrc: '/placeholder-vehicle.svg',
    alt: 'Brak zdjęcia',
    title: 'Brak zdjęcia'
  };

  protected annotationsControl = new FormControl<string>('');

  actionItems = [
    {
      icon: 'pi pi-send',
      label: 'Wydaj pojazd',
      command: () => {
        this.openVehicleReleaseModal();
      }
    },
    {
      icon: 'pi pi-key',
      label: 'Przypisz kluczyk',
      command: () => {
        this.openVehicleKeyAssignModal();
      }
    }
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly locationFacade: LocationFacade,
    private readonly sanitizer: DomSanitizer,
    private readonly destroyRef: DestroyRef,
    private readonly dialogService: DynamicDialogService,
    private readonly vehicleFacade: VehicleFacade
  ) {
    effect(() => {
      this.locationFacade.geocodingLocation$
        .pipe(
          filter((location): location is Geocoding => location !== null),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(location => {
          const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://maps.google.com/maps?q=${location.lat},${location.lng}&z=15&output=embed`
          );
          this.iframeUrl.set(safeUrl);
        });
    });
  }

  public ngOnInit(): void {
    const vehicleId = this.route.snapshot.paramMap.get('id');

    if (!vehicleId) {
      return;
    }

    this.vehicleId.set(vehicleId);

    this.vehicleFacade.loadVehicle(vehicleId);
    this.vehicleFacade.loadVehicleImages(vehicleId);

    this.vehicleFacade.vehicle$
      .pipe(
        filter((vehicle): vehicle is Vehicle => vehicle !== null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(vehicle => {
        this.vehicle.set(vehicle);
        this.statusHistory = this.buildStatusHistory(vehicle);
        this.locationFacade.getGeocodedLocation(vehicle.storage.address);
        this.loadAnnotations(vehicle);
        this.setupDetails(vehicle);
      });

    this.vehicleFacade.vehicleImages$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(images => {
        this.images = this.resolveGalleryImages(images);
      });
  }

  protected get orchestrator(): VehicleFacade {
    return this.vehicleFacade;
  }

  private resolveGalleryImages(images: ItemImageDto[]): VehicleGalleryImage[] {
    if (!images || images.length === 0) {
      return [this.placeholderImage];
    }

    return images
      .sort((a, b) => (a.imageNumber ?? 0) - (b.imageNumber ?? 0))
      .map((image, index) => ({
        id: image.id,
        itemImageSrc: image.publicUrl,
        thumbnailImageSrc: image.publicUrl,
        alt: `Zdjęcie ${index + 1}`,
        title: `Zdjęcie ${index + 1}`
      }));
  }

  protected onUploadSelect(event: FileSelectEvent): void {
    const files = event?.files as unknown;

    if (Array.isArray(files)) {
      this.selectedUploadImages = files;
      return;
    }

    if (files && true) {
      this.selectedUploadImages = Array.from(files as FileList);
      return;
    }

    this.selectedUploadImages = files ? [files as File] : [];
  }

  protected onUploadClear(): void {
    this.selectedUploadImages = [];
  }

  protected uploadImages(): void {
    const vehicleId = this.vehicleId();
    if (!vehicleId) return;

    this.vehicleFacade.uploadVehicleImages(vehicleId, this.selectedUploadImages);
    this.onUploadClear();
    this.imagesUpload?.clear();
  }

  protected deleteImage(imageId: number): void {
    const vehicleId = this.vehicleId();
    if (!vehicleId) return;

    this.vehicleFacade.deleteVehicleImage(vehicleId, imageId);
  }

  private loadAnnotations(vehicle: Vehicle) {
    if (vehicle.hasAnnotations) {
      this.annotationsControl.setValue(vehicle.annotations);
    }
  }

  protected get locationOrchestrator(): LocationFacade {
    return this.locationFacade;
  }

  private buildStatusHistory(vehicle: Vehicle): VehicleStatusEvent[] {
    const events: VehicleStatusEvent[] = [];

    if (vehicle.returnDate) {
      events.push({
        status: `Przyjęto od: ${vehicle.returningPerson || 'nieznany'}`,
        date: vehicle.returnDate,
        icon: 'tablerTruckDelivery',
      });
    }

    if (vehicle.releaseDate) {
      events.push({
        status: `Wydano do: ${vehicle.receiver || 'nieznany'}`,
        date: vehicle.releaseDate,
        icon: 'tablerCircleKey',
      });
    }

    const technicalInspection = vehicle.properties.find(
      p => p.propertyType === AvailablePropertyType.TECHNICAL_INSPECTION_DATE
    );
    if (technicalInspection?.value) {
      events.push({
        status: 'Przegląd techniczny',
        date: DateTime.fromISO(technicalInspection.value),
        icon: 'tablerEngine',
      });
    }

    const insuranceExpiry = vehicle.properties.find(
      p => p.propertyType === AvailablePropertyType.INSURANCE_EXPIRY_DATE
    );
    if (insuranceExpiry?.value) {
      events.push({
        status: 'Wygaśnięcie ubezpieczenia',
        date: DateTime.fromISO(insuranceExpiry.value),
        icon: 'tablerShieldExclamation',
      });
    }

    return events.sort((a, b) => b.date.toMillis() - a.date.toMillis());
  }

  private openVehicleReleaseModal() {
    const dialogRef = this.dialogService.open<void, VehicleReleaseModalResult>(
      VehicleReleaseModalComponent,
      {
        header: 'Wydawanie pojazdu',
        closable: true,
        draggable: false,
        width: '90vw',
        styleClass: 'max-w-[900px]',
        breakpoints: {'768px': '90vw', '480px': '95vw'},
        dismissableMask: true
      });

    dialogRef.afterClosed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result && this.vehicleId()) {
          this.vehicleFacade.releaseVehicle(this.vehicleId() as string, result)
        } else {
          throw new Error(`[${VehicleDetailsComponent.name}] Unable to open vehicle release modal. Vehicle ID is not set or result is null.`);
        }
      });
  }

  private openVehicleKeyAssignModal() {
    const vehicle = this.vehicle();

    if (!vehicle) {
      throw new Error(`[${VehicleDetailsComponent.name}] Vehicle is not set`);
    }

    const data: VehicleKeyAssignModalData = {
      storageId: vehicle.storage.id
    }

    const dialogRef = this.dialogService.open<VehicleKeyAssignModalData, VehicleKeyAssignModalResult>(
      VehicleKeyAssignModalComponent,
      {
        header: 'Przypisywanie numeru kluczyka',
        closable: true,
        draggable: false,
        styleClass: 'max-w-[900px]',
        breakpoints: {'768px': '90vw', '480px': '300px'},
        dismissableMask: true,
        data: data
      });

    dialogRef.afterClosed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (result) {
          const vehicleId = vehicle.id;
          const keyNumber = result?.keyNumber;

          if (vehicleId || keyNumber) {
            this.vehicleFacade.assignKeyNumber(Number(vehicleId), Number(keyNumber))
          } else {
            throw new Error(`[${VehicleDetailsComponent.name}] Unable to open vehicle key assign modal. Vehicle ID is not set or result is null.`);
          }
        }
      });
  }

  protected onAnnotationChange($event: EditorTextChangeEvent): void {
    this.annotationsChanged() || this.annotationsChanged.set(true);
  }

  protected onSubmit(): void {
    const annotations = this.annotationsControl.value;
    if (this.vehicleId() && annotations) {
      const vehicleId = this.vehicleId() as string;
      this.vehicleFacade.publishAnnotations(vehicleId, annotations);
    } else {
      console.error(`[${VehicleDetailsComponent.name}] Unable to submit vehicle annotations. Vehicle ID is not set or annotations are empty.`);
    }
  }

  protected onClearAnnotations(): void {
    this.annotationsControl.setValue('');
    this.annotationsChanged.set(false);
  }

  protected toggleGalleriaFullscreen() {
    this.fullscreenMode = true;
  }

  private setupDetails(vehicle: Vehicle) {
    this.details = [
      {
        icon: 'tablerNumber123',
        label: 'Nr. rejestracyjny',
        value: vehicle.plateNumber
      },
      {
        icon: 'tablerLabel',
        label: 'Kategoria',
        value: vehicle.categoryName
      },
      {
        icon: 'tablerKey',
        label: 'Nr. kluczyka',
        value: vehicle.displayKeyNumber
      },
      {
        icon: 'tablerFolder',
        label: 'Nr. dokumentów',
        value: vehicle.displayDocumentNumber
      },
      {
        icon: 'tablerArchive',
        label: 'Nr. archiwalny',
        value: vehicle.archiveNumber
      },
      {
        icon: 'tablerUserCircle',
        label: 'Właściciel',
        value: vehicle.owner.name
      }]
  }
}
